/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Highlighter from 'react-highlight-words';

// Component imports
import { Button, Col, Dropdown, Grid, Input, Menu, Modal, Popconfirm, Row, Space, Table, Tooltip, Typography } from 'antd';
import { EllipsisOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';
import Permissioned from '../Permissioned';
import BackupForm from '../Forms/BackupForm';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { PERMISSIONS_GROUP_EDITOR, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { useCreateBackupMutation, useDeleteBackupMutation, useFetchBackupsQuery, useTriggerBackupRestoreMutation } from '../../store/backups/backupsApi';
import { truncate } from '../../utils/string/truncate';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const { useBreakpoint } = Grid;

const BackupsList = () => {
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = screens.xs;

  const { data: backups, isFetching: isFetchingBackups } = useFetchBackupsQuery({ orgId, tenantId });
  const [, { isLoading: isCreatingBackup }] = useCreateBackupMutation({ fixedCacheKey: 'createBackup-shared' });
  const [triggerBackupRestore, { isLoading: isRestoringBackup }] = useTriggerBackupRestoreMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteBackup, { isLoading: isDeletingBackup }] = useDeleteBackupMutation();

  const [backupTableData, setBackupTableData] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  useEffect(() => {
    const filteredData = backups?.items.filter(i => {
      if (!i.description && searchFilter === '') {
        return true;
      }
      return i.description?.includes(searchFilter);
    });
    setBackupTableData(filteredData);
  }, [searchFilter, backups?.items]);

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const onDeleteClick = async(id) => {
    deleteBackup({ orgId, tenantId, backupId: id });
  };

  const onRestoreClick = async (record) => {
    let tenantBackupDate = moment(record.createdAt).format(DEFAULT_DATETIME_FORMAT);
    triggerBackupRestore({ orgId, tenantId, backupDate: tenantBackupDate, backupId: record.id });
  };

  const onBackupCreate = () => {
    closeCreateModal();
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  const columns = useMemo(() => {
    const _columns = [
      {
        title: t('Date'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: createdAt => {
          return moment(createdAt).format(DEFAULT_DATETIME_FORMAT);
        },
        sorter: {
          compare: (a, b) => a.createdAt - b.createdAt
        }
      },
      {
        title: t('ID'),
        dataIndex: 'id',
        key: 'id',
        responsive: ['sm']
      },
      {
        title: t('Note'),
        dataIndex: 'description',
        key: 'description',
        responsive: ['md'],
        render: note => {
          if (!note) {
            return <Typography.Text type="secondary">{t('(no note set)')}</Typography.Text>;
          }
          return (
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchFilter]}
              autoEscape
              textToHighlight={truncate(note, 150, '...')}
            />
          );
        }
      }
    ];

    if (userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
      _columns.push(
        {
          title: t('Actions'),
          key: 'actions',
          width: 100,
          align: 'center',
          render: record => {
            return (
              <Space>
                {
                  !isMobileSize
                    ? (
                      <>
                        <Popconfirm
                          title={t('Are you sure you want to restore this backup?')}
                          okText={t('Yes')}
                          cancelText={t('No')}
                          placement="topRight"
                          onConfirm={() => onRestoreClick(record)}
                        >
                          <Tooltip overlayClassName="pointer-events-none" title={t('Restore Backup')}>
                            <Button
                              type="text"
                              shape="circle"
                              icon={<HistoryOutlined />}
                              hidden={isMobileSize}
                              loading={isRestoringBackup}
                            />
                          </Tooltip>
                        </Popconfirm>

                        <Popconfirm
                          title={t('Are you sure you want to delete this backup?')}
                          okText={t('Yes')}
                          cancelText={t('No')}
                          placement="topRight"
                          onConfirm={() => onDeleteClick(record.id)}
                        >
                          <Tooltip overlayClassName="pointer-events-none" title={t('Delete Backup')}>
                            <Button
                              shape="circle"
                              type="text"
                              icon={<DeleteIcon />}
                              hidden={isMobileSize}
                              loading={isDeletingBackup}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </>
                    )
                    : (
                      <Dropdown
                        trigger="click"
                        placement="bottomRight"
                        overlay={
                          <Menu
                            onClick={(item) => {
                              switch (item.key) {
                                case 'restore':
                                  onRestoreClick(record);
                                  break;
                                case 'delete':
                                  onDeleteClick(record.id);
                                  break;
                                default:
                                  break;
                              }
                            }}
                            items={[
                              {
                                key: 'restore',
                                icon: <HistoryOutlined className="fs-xl mb-2 mt-2 ml-2"/>,
                                label: ''
                              }, {
                                key: 'delete',
                                icon: <DeleteIcon className="fs-xl mb-2 mt-2 ml-2" />,
                                label: ''
                              }
                            ]}
                          />
                        }
                      >
                        <Button
                          shape="circle"
                          type="text"
                          icon={<EllipsisOutlined className="fw-bold fs-xxl" rotate={90}/>}
                          loading={isDeletingBackup || isRestoringBackup}
                        />
                      </Dropdown>
                    )
                }
              </Space>
            );
          }
        }
      );
    }

    return [..._columns];

  }, [backups, isMobileSize, searchFilter]);

  // Backup table columns

  return (
    <Fragment>
      <Row gutter={24} wrap={screens.xs && !screens.sm}>
        <Col flex="auto" className="mb-2">
          <Input
            size="large"
            style={{ maxWidth: screens.xs ? '100%' : 280 }}
            placeholder={t('Search in contents')}
            allowClear
            prefix={<SearchOutlined className="text-muted" />}
            className="w-100"
            onChange={(e) => {
              setSearchFilter(e.target.value);
            }}
          />
        </Col>

        <Col
          flex={screens.xs ? 'auto' : 'none'}
          className="mb-2"
        >
          <Permissioned
            allowedPermissions={PERMISSIONS_GROUP_EDITOR}
            currentPermissions={userPermissions}
            fallbackComponent={null}
          >
            <Button
              size="large"
              type="primary"
              ghost
              icon={<PlusIcon/>}
              className="mb-2 float-right"
              loading={isCreatingBackup}
              onClick={() => openCreateModal()}
            >
              {t('Create Backup Now')}
            </Button>
          </Permissioned>
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="small"
        pagination={{
          size: 'default',
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => `${t('Total Backups')}: ${total}`
        }}
        dataSource={backupTableData}
        columns={columns}
        loading={isFetchingBackups}
        onChange={handleTableChange}
      />

      <Modal
        title={t('Do you want to add a note?')}
        open={isCreateModalOpen}
        onOk={closeCreateModal}
        onCancel={closeCreateModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
      >
        <BackupForm onSuccess={onBackupCreate} />
      </Modal>
    </Fragment>
  );
};

export default BackupsList;
